<template>
	<div id="app" class="h-100 d-flex align-content-between flex-wrap text-dark">
		<header id="header" class="shadow-sm" :class="(!clientKey) ? 'invisible': ''">
			<transition name="fade" key="transition.header" appear>
				<b-navbar ref="mainHeader" sticky toggleable="lg" type="light" variant="light">
					<b-navbar-brand to="/" v-if="clientConfig && clientConfig.name"><img :src="logo" style="height: 48px;" :alt="clientConfig.name"></b-navbar-brand>
					<div class="navbar-nav ml-lg-auto" v-if="profile">
						<span class="navbar-text mb-0 pb-0"><transition name="fade" appear><h1 class="h3">{{ profile.firstName }} {{ profile.lastName }}</h1></transition></span>
					</div>
				</b-navbar>
			</transition>
		</header>
		<main id="body" class="w-100 d-flex overflow-auto justify-content-center">
			<template v-if="isReady && ((profile && profile.uuid) || (/^demo/i.test($route.name)))">
				<transition name="fade" key="transition.main-content" appear>
					<div id="main-content" key="main-content">
						<router-view ref="view" :key="$route.fullPath" />
					</div>
				</transition>
			</template>
			<template v-else-if="isReady && !clientKey && !/^init/i.test($route.name)">
				<transition name="fade" key="transition.main-content" appear>
					<div id="main-content" key="main-content">
						<b-container class="w-75 text-dark h-100">
							<b-row class="heading h-100 m-0 p-0" align-v="center">
								<b-col class="text-center" style="margin-bottom: 64px;">
									<img :src="require('@/assets/propeller-color-icon.svg')" class="m-5" style="height: 256px; filter: drop-shadow(1px 1px 1px rgb(32,32,32));" alt="Propeller"><br/>
									<h1 class="h4">Oops!</h1>
									Well this is unexpected. Please contact your school's support desk or <a class="text-decoration-underline" href="mailto:help@propeller.cloud">email help@propeller.cloud</a>.
								</b-col>
							</b-row>
						</b-container>
					</div>
				</transition>
			</template>
			<template v-else-if="isReady && isMaintenanceMode">
				<transition name="fade" key="transition.main-content" appear>
					<div id="main-content" key="main-content" :class="((!maintenanceMessages.length) ? 'my-auto' : undefined)">
						<b-container class="w-75 text-dark h-100">
							<b-row class="heading m-0 mx-auto p-0" style="width: 320px; height: 320px;" align-v="center">
								<b-col class="text-center">
									<img :src="require('@/assets/propeller-color-icon.svg')" class="my-4" style="height: 256px; filter: drop-shadow(1px 1px 1px rgb(32,32,32));" alt="Propeller"><br/>
								</b-col>
							</b-row>
							<b-row class="m-0 p-0">
								<b-col class="text-center">
									<p class="h2">Sorry, we'll be back in a bit!</p>
									<p>The system is currently undergoing routine maintenance. Please check back soon.</p>
									<p>Thank you for your patience!</p>
									<b-container class="w-75 mt-5 small border border-warning shadow-sm" v-if="maintenanceMessages.length">
										<b-row class="bg-warning font-weight-bold text-left p-2">
											<b-col>
												Updates
											</b-col>
										</b-row>
										<template v-for="m in maintenanceMessages">
											<b-row class="border-bottom p-2">
												<b-col style="min-width: 30%; max-width: 30%;">{{ $dayjs(m.date).format('YYYY/MM/DD HH:mm:ss') }}</b-col>
												<b-col class="w-auto text-left">{{ m.message }}</b-col>
											</b-row>
										</template>
									</b-container>
								</b-col>
							</b-row>
						</b-container>
					</div>
				</transition>
			</template>
			<elumin-loader v-else />
		</main>
		<footer id="footer" class="bg-dark text-white">
			<b-row no-gutters>
				<b-col class="text-center">
					<span id="powered-by-notice" class="mx-1" :class="(!shouldShowPoweredBy) ? 'd-none': ''">Powered by <a href="https://www.elumin.co"><img :src="require(`@/assets/elumin-logo-white.svg`)" style="max-height: 12px;" alt="eLumin"></a></span>
					<span id="copyright-notice" class="mx-1" :class="(!shouldShowCopyrightFooter) ? 'd-none': ''">&copy; {{ (new Date()).getFullYear() }}, eLumin LLC, All rights reserved.</span>
					<span>v{{ build.version }} ({{ build.date }})</span>
				</b-col>
			</b-row>
		</footer>
		<div role="alert"><ErrorModal :ref="errorModalData.id" :modalData="errorModalData" /></div>
		<template v-if="clientConfig.clientCode">
			<div role="alert">
				<b-modal static id="UnknownUserErrorModal" :visible="profile && !profile.uuid" header-bg-variant="danger" header-text-variant="light" no-close-on-backdrop no-close-on-esc hide-header-close hide-footer>
					<div slot="modal-title">Error</div>
					<b-container class="text-center">
						<b-row>
							<b-col class="text-justify">Sorry {{ profile.firstName }}! We could not find an enrollment record for you. If this is unexpected, please contact your school's support desk or <a :href="'mailto:help@propeller.cloud'">email help@propeller.cloud</a>.</b-col>
						</b-row>
					</b-container>
				</b-modal>
			</div>
		</template>
	</div>
</template>

<script>
	import Vue from 'vue';
	import VueMeta from 'vue-meta';
	import VueTimers from 'vue-timers';
	
	import { mapMutations, mapState } from 'vuex';
	import { library } from '@fortawesome/fontawesome-svg-core';
	import { faCaretDown, faCaretRight, faQuestion, faFrown } from '@fortawesome/free-solid-svg-icons';
	
	Vue.use(
		VueMeta,
		{
			refreshOnceOnNavigation: true
		}
	);
	Vue.use(VueTimers);
	
	library.add(faCaretDown, faCaretRight, faQuestion);
	
	let listeners = [];
	
	const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
	
	export default
	{
		name: 'app',
		metaInfo()
		{
			const title = ((this.whiteLabelTitle) ? '' : 'Propeller');
			
			return {
				title,
				titleTemplate: '%s Platform Portal'
			};
		},
		computed:
		{
			...mapState([ 'clientKey', 'clientConfig', 'authSession', 'userSession', 'profile', 'loadedAt' ]),
			
			appName: function()
			{
				return (process.env.VUE_APP_NAME || ((window.location.hostname != 's3.amazonaws.com') ? window.location.hostname.split('.')[0] : (new URL(window.location.href)).pathname.split('/')[2]));
			},
			apiURL: function()
			{
				return (process.env.VUE_APP_CLIENT_API || this.clientConfig.apiURLs[this.appName]) + '/ck:' + this.clientKey;
			},
			apiAuthURL: function()
			{
				return (process.env.VUE_APP_CLIENT_AUTH_API || this.clientConfig.apiURLs.auth) + '/ck:' + this.clientKey;
			},
			apiCoreURL: function()
			{
				return process.env.VUE_APP_ROOT_API + '/ck:' + this.clientKey;
			},
			loginURL: function()
			{
				return process.env.VUE_APP_CLIENT_LOGIN_URL || this.clientConfig.loginURL;
			},
			coreAPI: function()
			{
				const instance = this.$http.create(
					{
						baseURL: this.apiCoreURL,
						timeout: 1000 * 30,
						withCredentials: true
					}
				);
				
				return instance;
			},
			authAPI: function()
			{
				const instance = this.$http.create(
					{
						baseURL: this.apiAuthURL,
						timeout: 1000 * 30,
						withCredentials: true,
						headers: { common: { 'eluminSession': this.authSession } }
					}
				);
				
				return instance;
			},
			portalAPI: function()
			{
				const instance = this.$http.create(
					{
						baseURL: this.apiURL,
						timeout: 1000 * 30,
						withCredentials: true,
						headers: { common: { 'eluminSession': this.userSession } }
					}
				);
				
				instance.interceptors.request.use(
					async function(c)
					{
						const randomTime = Math.floor(Math.random() * (5000 - 2000 + 1) + 2000);
						
						console.debug('Sleep for ' + randomTime);
						
						await sleep(randomTime);
						
						return c;
					},
					null,
					{
						runWhen: function(c)
						{
							if(/\/link$/.test(c.url))
								return true;
							
							return false;
						}
					}
				);
				
				return instance;
			},
			build: function()
			{
				return { version: _ELUMIN_BUILD_VERSION, date: _ELUMIN_BUILD_DATE };
			},
			logo: function()
			{
				let logo = undefined;
				
				try
				{
					logo = 'https://s3.amazonaws.com/co.elumin.sites.prd/config/' + this.clientKey[0] + '/' + this.clientKey[1] + '/' + this.clientKey + '/logo.png'
				}
				catch(e)
				{
					logo = require('@/assets/elumin.svg');
				}
				
				return logo;
			},
			whiteLabelTitle: function()
			{
				return (('whiteLabel' in this.clientConfig) && this.clientConfig.whiteLabel.title);
			},
			shouldShowPoweredBy: function()
			{
				if(('whiteLabel' in this.clientConfig) && ('poweredBy' in this.clientConfig.whiteLabel))
					return !this.clientConfig.whiteLabel.poweredBy;
				
				return true;
			},
			shouldShowCopyrightFooter: function()
			{
				if(('whiteLabel' in this.clientConfig) && ('copyrightFooter' in this.clientConfig.whiteLabel))
					return !this.clientConfig.whiteLabel.copyrightFooter;
				
				return true;
			},
			isMaintenanceMode: function()
			{
				if(('maintenance' in this.clientConfig))
					return this.clientConfig.maintenance.enabled
				
				return false;
			},
			maintenanceMessages: function()
			{
				if(!this.isMaintenanceMode || (!('messages' in this.clientConfig.maintenance)))
					return [];
				
				let messages = this.clientConfig.maintenance.messages;
				
				messages.sort((a,b) => ((a.date >= b.date) ? 1 : -1));
				
				return messages.reverse();
			}
		},
		methods:
		{
			init: async function init()
			{
				this.$logger.info('Initializing ' + this.appName.replace(/^./, (c) => c.toUpperCase()) + '...');
				
				if(/^init/i.test(this.$route.name))
				{
					await this.$store.dispatch('clear');
					this.userToken = null;
					
					let params = this.$route.params;
					
					if(('clientKey' in params))
					{
						if(params.clientKey)
						{
							this.$logger.debug('Found clientKey: ' + params.clientKey);
							
							await this.$store.dispatch('clientKey', params.clientKey);
						}
					}
				}
				
				if(!this.clientKey)
					return;
				
// 				if(!('clientCode' in this.clientConfig))
// 				{
// 					const clientConfig = await this.coreAPI.get('/client')
// 						.then(
// 							resp => resp.data
// 						).catch(
// 							error =>
// 							{
// 								if(error.response)
// 								{
// 									return error.response.data;
// 								}
// 								else if(error.request)
// 								{
// 									this.$logger.error(error.request);
// 							
// 									return { code: 'NoResponse', message: 'No response received' };
// 								}
// 								else
// 								{
// 									this.$logger.error('Error', error.message);
// 							
// 									return { code: 'Error', message: error.message };
// 								}
// 							}
// 						);
// 				
// 					if(!clientConfig || clientConfig.status != 'OK')
// 					{
// 						this.showError('<p>There was an error setting up the application. Please contact support if this error persists.</p>Code: ' + clientConfig.code + '<br/>Message: ' + clientConfig.message);
// 				
// 						return;
// 					}
// 				
// 					await this.$store.dispatch('clientConfig', clientConfig.config);
// 				}
				
				await this.getClientConfig();
				
// 				this.$logger.debug(this.clientConfig);
				
				if(('maintenance' in this.clientConfig))
					return;
				
				if(this.$route.params.userToken)
				{
					this.$logger.debug('Found userToken: ' + this.$route.params.userToken);
					
					this.userToken = this.$route.params.userToken;
				}
				
				if(!this.userToken)
				{
					if(await this.getUserToken())
						this.$logger.debug('userToken: ' + this.userToken);
				}
				
				if(this.userToken)
				{
					await this.startSession();
					await this.getProfile();
				}
				
// 				if((this.isSessionValid || (this.profile && 'uuid' in this.profile && this.profile.uuid)) && /^init/i.test(this.$route.name))
				if(/^init/i.test(this.$route.name) && this.clientKey)
				{
					if('rs' in this.$route.query)
						this.$router.push({ path: this.$route.query.rs });
					else
						this.$router.push({ path: '/' });
				}
				
				return;
			},
			loginRedirect: function loginRedirect()
			{
				if(!this.loginURL)
					return this.showError(error.response.statusText);
				
				let redirectURL = this.loginURL + 
					encodeURIComponent(
						(new URL(this.apiAuthURL, window.location.origin)).pathname + '/session/create/' + this.appName + '?rs=' + 
						encodeURIComponent(
							window.location.origin + 
							window.location.pathname +
							this.$router.resolve(
								{
									name: 'Init-ClientKey-UserToken',
									params: { clientKey: this.clientKey, userToken: '|token|' },
									query: ((/^Init-ClientKey/.test(this.$route.name)) ? this.$route.query : { rs: this.$route.fullPath })
								}
							).href
						)
					)
		
				this.$logger.debug('Redirecting to: ' + redirectURL);
				
				window.location.href = redirectURL;
		
				return;
			},
			getUserToken: async function getUserToken()
			{
				let token = await this.authAPI.get('/session/create/' + this.appName)
					.then(
						resp => { return resp.headers.eluminauthtoken; }
					).then(
						token =>
						{
							if(!token)
							{
								this.showError('Unable to retrieve user authentication token');
						
								return;
							}
					
							return token;
						}
					).catch(
						error =>
						{
							if(error.response)
							{
								if(error.response.status === 403)
								{
									this.showError(error.response.data.code);
							
									return;
								}
								
								return this.loginRedirect();
							}
							else if(error.request)
							{
								this.$logger.error(error.request);
						
								this.showError('<p>There was an error setting up the application. Please contact support if this error persists.</p>');
						
								return;
							}
							else
							{
								this.showError('<p>There was an error setting up the application. Please contact support if this error persists.</p>Message: ' + error.message);
						
								return;
							}
						}
					);
				
				return this.userToken = token;
			},
			startSession: async function startSession()
			{
				let r = await this.portalAPI.post('/session/start', { token: this.userToken })
					.then(
						async resp =>
						{
							if('eluminauthsession' in resp.headers)
							{
								this.$logger.debug('Found authSession: ', resp.headers.eluminauthsession);
								
								await this.$store.dispatch('authSession', resp.headers.eluminauthsession);
								
								this.$logger.debug('Stored authSession: ', this.authSession);
							}
							
							if('eluminsession' in resp.headers)
							{
								this.$logger.debug('Found userSession: ', resp.headers.eluminsession);
								
								await this.$store.dispatch('userSession', resp.headers.eluminsession);
								
								this.$logger.debug('Stored userSession: ', this.userSession);
							}
							
							return;
						}
					).catch(
						async error =>
						{
							this.$logger.debug(error);
							
							if(error.response)
							{
								await this.$store.dispatch('authSession', "");
								await this.$store.dispatch('userSession', "");
								
								return;
							}
							else if(error.request)
							{
								this.$logger.error(error.request);
								
								return { code: 'NoResponse', message: 'No response received' };
							}
							else
							{
								this.$logger.error('Error', error.message);
								
								return { code: 'Error', message: error.message };
							}
						}
					);
				
				if(r && 'message' in r)
				{
					this.showError('<p>There was an error setting up the application. Please contact support if this error persists.</p>Message: ' + r.message);
				}
				
				return r;
			},
			getProfile: async function getProfile()
			{
				let r = await this.portalAPI.get('/session')
					.then(
						async resp => await this.$store.dispatch('profile', resp.data)
					).catch(
						async error =>
						{
							if(error.response)
							{
								return await this.$store.dispatch('profile', null);
							}
							else if(error.request)
							{
								this.$logger.error(error.request);
								
								return { code: 'NoResponse', message: 'No response received' };
							}
							else
							{
								this.$logger.error('Error', error.message);
								
								return { code: 'Error', message: error.message };
							}
						}
					);
				
				if(r && 'message' in r)
				{
					this.showError('<p>There was an error setting up the application. Please contact support if this error persists.</p>Message: ' + r.message);
				}
				
				return r;
			},
			getClientConfig: async function getClientConfig()
			{
				const clientConfig = await this.coreAPI.get('/client')
					.then(
						resp => { return Promise.resolve(resp.data); }
					).catch(
						error =>
						{
							if(error.response)
							{
								return Promise.reject(error.response.data);
							}
							else if(error.request)
							{
								this.$logger.error(error.request);
						
								return Promise.reject({ code: 'NoResponse', message: 'No response received' });
							}
							else
							{
								this.$logger.error('Error', error.message);
						
								return Promise.reject({ code: 'Error', message: error.message });
							}
						}
					);
				
				if(!clientConfig || clientConfig.status != 'OK')
				{
					this.showError('<p>There was an error setting up the application. Please contact support if this error persists.</p>Code: ' + clientConfig.code + '<br/>Message: ' + clientConfig.message);
					
					return;
				}
				
				await this.$store.dispatch('clientConfig', clientConfig.config);
			},
			showError: function(message)
			{
				let data = this.errorModalData;
				
				this.$logger.error(message);
				data.message = message;
				
				this.$root.$emit('bv::show::modal', data.id);
				
				return false;
			},
			slugify: function(text)
			{
				return text.toString().toLowerCase()
					.replace(/\s+/g, '-')  
					.replace(/[^\w\-]+/g, '') 
					.replace(/\-\-+/g, '-')  
					.replace(/^-+/, '')  
					.replace(/-+$/, ''); 
			}
		},
		created()
		{
			let vm = this;
			
			if(/^demo/i.test(this.$route.name))
				return;
			
			this.$nextTick(this.init);
		},
		mounted()
		{
			this.$store.commit('loadedAt', new Date());
	
			this.isReady = true;
		},
		beforeDestroy()
		{
			listeners.forEach(
				(v) =>
				{
					this.$logger.debug('Deregistering listener: ' + v);
					
					this.$root.$off(v);
				}
			);
		},
		watch:
		{
			'$route.params': function(params)
			{
				if(/^init/i.test(this.$route.name))
					this.$nextTick(this.init);
			},
			
			clientConfig: async function(nVal, oVal)
			{
				if(('maintenance' in nVal) && nVal.maintenance.enabled)
				{
					if(!this.timers.refreshClientConfig.isRunning)
						this.$timer.start('refreshClientConfig');
				}
				
				if((!('maintenance' in nVal) || (!nVal.maintenance.enabled)) && (('maintenance' in oVal) && (oVal.maintenance.enabled)))
				{
					if(this.timers.refreshClientConfig.isRunning)
						this.$timer.stop('refreshClientConfig');
					
					this.$router.go();
				}
				
				return true;
// 			},
// 			
// 			clientKey: async function(nVal, oVal)
// 			{
// 				if(!!nVal)
// 					this.$nextTick(this.getClientConfig);
// 				
// 				return true;
			}
		},
		timers:
		{
			refreshApp:
			{
				callback: function()
				{
					if((new Date() - this.loadedAt) > (1000 * 60 * 60))
						this.$router.go();
				},
				autostart: true,
				repeat: true,
				immediate: false,
				time: 1000 * 60 * 5
			},
			refreshClientConfig:
			{
				callback: function() { this.getClientConfig(); },
				autostart: false,
				repeat: true,
				immediate: true,
				time: 1000 * 60
			}
		},
		data()
		{
			return {
				isReady: false,
				userToken: null,
				errorModalData:
				{
					id: 'errorModal',
					message: undefined,
					allowClose: false
				}
			}
		}
	}
</script>

<style scoped>
	a
	{
		text-decoration: underline;
	}	
</style>

<style>
	html, body, #app
	{
		font-family: 'GothamPro', sans-serif !important;
		height: 100% !important;
		min-height: 100% !important;
	}
	
	#header
	{
		min-height: 64px !important;
		/* max-height: 64px !important; */
		min-width: 100% !important;
	}
	
	#main-content
	{
		width: 100% !important;
		/* max-width: calc(100% - 256px) !important; */
		overflow: auto !important;
		padding: 12px !important;
	}
		
	#footer
	{
		/* position: relative !important; */
		min-height: 32px !important;
		/* max-height: 32px !important; */
		min-width: 100% !important;
		overflow: visible !important;
		line-height: 30px !important;
		font-size: 12px !important;
	}

	.navbar-text
	{
		color: var(--dark) !important;
	}
	
	.btn:focus
	{
		box-shadow: none !important;
		outline: auto 1px rgba(0, 0, 0, 0.9) !important;
	}

	.fade-enter-active, .fade-leave-active
	{
		transition: all .5s !important;
	}
	
	.fade-enter, .fade-leave-to
	{
		opacity: 0 !important;
	}
</style>
